import { useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LoginIcon from '@mui/icons-material/Login';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { logoutUser } from '../../../actions/userAction';
import { useLang, uiText } from '../../../utils/i18n';

const MobileMenu = ({ isOpen, onClose, isAuthenticated, user }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { lang } = useLang();

    // Locks background scroll while the drawer is open
    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    const handleLogout = () => {
        dispatch(logoutUser());
        onClose();
        navigate('/login');
        enqueueSnackbar(uiText('logoutSuccess', lang), { variant: 'success' });
    };

    const menuItems = [
        { label: uiText('navHome', lang), icon: <StorefrontOutlinedIcon sx={{ fontSize: '20px' }} />, to: '/' },
        { label: uiText('navShop', lang), icon: <ShoppingBagOutlinedIcon sx={{ fontSize: '20px' }} />, to: '/products' },
        { label: uiText('wishlist', lang), icon: <FavoriteBorderIcon sx={{ fontSize: '20px' }} />, to: '/wishlist' },
        { label: uiText('myAccount', lang), icon: <AccountCircleOutlinedIcon sx={{ fontSize: '20px' }} />, to: '/account' },
        { label: uiText('navMyOrders', lang), icon: <ReceiptLongOutlinedIcon sx={{ fontSize: '20px' }} />, to: '/orders' },
        { label: uiText('downloadApp', lang), icon: <DownloadIcon sx={{ fontSize: '20px' }} />, to: '/download-app' },
    ];

    return (
        <>
            {/* backdrop */}
            <div
                className={`fixed inset-0 bg-black/40 z-40 sm:hidden transition-opacity ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
                onClick={onClose}
            />

            {/* panel */}
            <div
                className={`fixed top-0 left-0 h-full w-[78%] max-w-xs bg-white z-50 sm:hidden shadow-2xl flex flex-col transition-transform duration-300 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
            >
                <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b">
                    <div className="flex items-center gap-3">
                        <span className="w-11 h-11 rounded-full bg-primary-blue text-white flex items-center justify-center font-bold text-lg flex-shrink-0">
                            {isAuthenticated && user?.name ? user.name.charAt(0).toUpperCase() : '?'}
                        </span>
                        <div>
                            <p className="font-semibold text-gray-800 leading-tight">
                                {isAuthenticated && user?.name ? `${lang === 'zh' ? '歡迎，' : 'Welcome, '}${user.name}` : uiText('welcomeGuest', lang)}
                            </p>
                            {isAuthenticated && user?.email && (
                                <p className="text-xs text-gray-400 truncate max-w-[160px]">{user.email}</p>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-500 p-1">
                        <CloseIcon />
                    </button>
                </div>

                <div className="flex flex-col py-2 overflow-y-auto flex-1">
                    {user?.role === 'admin' && (
                        <Link to="/admin/dashboard" onClick={onClose} className="flex items-center gap-3 px-5 py-3.5 border-b hover:bg-gray-50 text-gray-700">
                            <span className="text-primary-blue"><DashboardIcon sx={{ fontSize: '20px' }} /></span>
                            Admin Dashboard
                        </Link>
                    )}
                    {menuItems.map((item) => (
                        <Link key={item.label} to={item.to} onClick={onClose} className="flex items-center gap-3 px-5 py-3.5 border-b hover:bg-gray-50 text-gray-700">
                            <span className="text-primary-blue">{item.icon}</span>
                            {item.label}
                        </Link>
                    ))}
                </div>

                <div className="border-t px-5 py-1">
                    {isAuthenticated ? (
                        <button onClick={handleLogout} className="w-full flex items-center gap-3 py-3.5 text-primary-green font-semibold">
                            <PowerSettingsNewIcon sx={{ fontSize: '20px' }} />
                            {uiText('logout', lang)}
                        </button>
                    ) : (
                        <Link to="/login" onClick={onClose} className="flex items-center gap-3 py-3.5 text-primary-green font-semibold">
                            <LoginIcon sx={{ fontSize: '20px' }} />
                            {uiText('loginSignUp', lang)}
                        </Link>
                    )}
                </div>
            </div>
        </>
    );
};

export default MobileMenu;